/**
 * Orchestrates HTML export: gathers state, encodes assets, calls template builder, triggers download.
 */

import { useProjectStore } from '@/store/projectStore.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { getBlob } from '@/previs/audioBlobStore.ts';
import { getClipAbsoluteStart } from '@/store/projectDerived.ts';
import { getEmbeddedDatabase, readAudioBytes } from '@/previs/sqlReader.ts';
import { buildPlayerHtml } from './playerTemplate.ts';
import { capabilities } from '@/capabilities.ts';
import { resolveGenerationInputImage } from '@/ai/generationInput.ts';
import { parseAspectRatio } from '@/utils/aspectRatio.ts';
import { shotMovementToNative } from '@/previs/converters.ts';
import { computeMovementArrowLayout } from '@/components/SceneBoard/shotMovementArrows.ts';

// ── Export data types (serializable — no AudioBuffer/ObjectURL) ─────────

/**
 * One camera-movement arrow, in percentages of the letterboxed SHOT FRAME — the same numbers
 * ShotMovementOverlay writes as inline styles. `sprite` indexes ExportData.arrowSprites: a type's
 * PNG is inlined once and shared, since a single movement can place five arrows.
 */
export interface ExportArrow {
  sprite: number;
  leftPct: number;
  topPct: number;
  widthPct: number;
  heightPct: number;
  transform: string;
}

export interface ExportShot {
  id: number;
  sceneId: number;
  number: string;
  name: string;
  order: number;
  duration: number;
  thumbnailDataUrl?: string;
  videoDataUrl?: string;
  notes?: string;
  focalLength: number;
  fStop: number;
  shotSize?: string;
  shotAngle?: string;
  shotMovement?: string;
  /** Precomputed here, not in the player: MOVEMENT_ARROW_SETUP has to stay re-synced with Unity, so
   *  it must exist in exactly one place. Absent when the movement has no overlay art. */
  movementArrows?: ExportArrow[];
}

export interface ExportScene {
  id: number;
  number: string;
  name: string;
  locationType: string;
  timeOfDay: string;
  color: string;
  shots: ExportShot[];
}

export interface ExportAudioClip {
  id: number;
  label: string;
  startTime: number;
  duration: number;
  volume: number;
  mimeType: string;
  lane: number;
  isMuted: boolean;
  fadeInTime: number;
  fadeOutTime: number;
  color: string;
  /** Seconds into the decoded buffer where playback starts — the trim offset. Mirrors
   *  useAudioPlayback's `time - absStart + bufferOffset`; a player that ignores it plays the
   *  untrimmed head of every left-trimmed or split clip. */
  bufferOffset: number;
  rawBase64: string | null;
  mediaUrl?: string | null;
  peaks: number[] | null;
}

export interface ExportTransition {
  id: number;
  fromShotId: number;
  toShotId: number;
  type: string;
  duration: number;
  easing: string;
  videoObjectUrl?: string;
}

export interface ExportComment {
  id: number;
  shotId: number | null;
  timecode: number | null;
  author: string;
  text: string;
  createdAt: string;
  resolvedAt: string | null;
  parentCommentId: number | null;
  /** Freehand doodle as JSON path data ({"paths":[{d,stroke,strokeWidth,opacity?}],"viewBox":…}),
   *  null when the comment carries no drawing. Declared rather than inherited by accident: the
   *  gather step below `filter`s store Comments through untouched, and the day that becomes a `map`
   *  an undeclared field goes silently missing from the payload — which is how the player lost the
   *  doodles once already. */
  annotationSvg?: string | null;
  /** Page number for a screenplay-page doodle. Such strokes are page geometry and must never be
   *  drawn over a shot — same exclusion mergedAnnotationForShot applies on the Unity sync. */
  screenplayPage?: number | null;
}

export interface ExportApproval {
  id: number;
  shotId: number;
  status: string;
  approvedBy: string;
  approvedAt: string;
  notes: string;
  revision: number;
}

export interface ExportData {
  title: string;
  aspectRatio: string;
  sensorSize: string;
  hasScreenplay: boolean;
  scenes: ExportScene[];
  totalDuration: number;
  transitions: ExportTransition[];
  audioClips: ExportAudioClip[];
  comments: ExportComment[];
  approvals: ExportApproval[];
  /** Arrow sprites referenced by ExportShot.movementArrows, deduped by type — only the ones this
   *  export's shots actually use, never all ten. */
  arrowSprites: string[];
}

// ── Chunked base64 encoder (avoids stack overflow on large Uint8Arrays) ─

export function uint8ToBase64(data: Uint8Array): string {
  const CHUNK = 8192;
  let result = '';
  for (let i = 0; i < data.length; i += CHUNK) {
    const slice = data.subarray(i, Math.min(i + CHUNK, data.length));
    result += String.fromCharCode(...slice);
  }
  return btoa(result);
}

// ── Image compression via offscreen canvas ──────────────────────────────
// Re-encodes a data URL through a canvas at reduced resolution + JPEG quality.
// Max dimension 960px, quality 0.7 — sharp enough for review, typically 3-5x smaller.

const COMPRESS_MAX_DIM = 960;
const COMPRESS_QUALITY = 0.7;

export function compressImageDataUrl(dataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;

      // Scale down if larger than max dimension
      if (width > COMPRESS_MAX_DIM || height > COMPRESS_MAX_DIM) {
        const scale = COMPRESS_MAX_DIM / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(dataUrl); return; }
      ctx.drawImage(img, 0, 0, width, height);
      let compressed: string;
      try {
        compressed = canvas.toDataURL('image/jpeg', COMPRESS_QUALITY);
      } catch {
        // Tainted canvas (a cross-origin/file:// source on a WebView without the file-access
        // preference). Resolving here is what keeps the promise from never settling — a throw
        // inside this handler would hang the whole export with no error surfaced.
        resolve(dataUrl);
        return;
      }
      // The length comparison only means anything when the INPUT is itself a data: URL.
      // blob: and file:// URLs are short strings regardless of the pixels behind them, so
      // comparing against them always "wins" and throws the real image away.
      if (!dataUrl.startsWith('data:')) {
        resolve(compressed);
      } else {
        resolve(compressed.length < dataUrl.length ? compressed : dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl); // fall back to original on error
    img.src = dataUrl;
  });
}

// ── file:// / blob: → portable data: URL ────────────────────────────────
// Embedded mode hands the store file:// URLs into Unity's thumbnail cache
// (TimelineWebViewPanel.SerializeShotUpdatesForSceneId / BuildShotThumbnailFileUrl). Those are
// meaningless the moment the HTML leaves the WebView, so every export path must convert them to
// bytes before writing. fetch() on file:// is proven in this WebView — store/actions.ts does it
// for generation-history assets at load, backed by SetAllowFileAccessFromFileURLs(true).

/** url → byte count once inlined as base64. Keyed by URL; Unity's ?v=<ticks> self-invalidates. */
const thumbByteEstimateCache = new Map<string, number>();

function blobToDataUrlString(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('readAsDataURL failed'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Returns a `data:` URL for `url`, or undefined when the bytes cannot be obtained.
 * Callers MUST drop the thumbnail on undefined — never emit the original reference, or the
 * export ships a broken <img> instead of the player's placeholder card.
 */
export async function materializeThumbnailDataUrl(url: string): Promise<string | undefined> {
  if (url.startsWith('data:')) return url;
  // Primary: read the real bytes (lossless, no canvas, no taint risk).
  try {
    const resp = await fetch(url);
    if (resp.ok) {
      const blob = await resp.blob();
      if (blob.size > 0) {
        thumbByteEstimateCache.set(url, Math.round(blob.size * 1.37));
        return await blobToDataUrlString(blob);
      }
    }
  } catch { /* fall through to the canvas route */ }
  // Secondary: canvas re-encode. Works where fetch(file:) is blocked but <img> loads.
  // Lossy (960px / q0.7) even when compressImages is off — a soft picture beats no picture.
  try {
    const viaCanvas = await compressImageDataUrl(url);
    if (viaCanvas.startsWith('data:')) return viaCanvas;
  } catch { /* ignore */ }
  return undefined;
}

/** Bytes this thumbnail will contribute to the exported HTML. 0 when unreadable. */
export async function measureThumbnailBytes(url: string): Promise<number> {
  if (url.startsWith('data:')) return url.length;
  const hit = thumbByteEstimateCache.get(url);
  if (hit !== undefined) return hit;
  try {
    const resp = await fetch(url);
    if (!resp.ok) { thumbByteEstimateCache.set(url, 0); return 0; }
    const blob = await resp.blob();
    const n = Math.round(blob.size * 1.37); // base64 overhead once inlined
    thumbByteEstimateCache.set(url, n);
    return n;
  } catch {
    thumbByteEstimateCache.set(url, 0);
    return 0;
  }
}

// ── Gather export data from store ────────────────────────────────────────

export async function gatherExportData(includeVideo: boolean, compressImages: boolean = true, includeDoodles: boolean = false): Promise<ExportData> {
  const projectState = useProjectStore.getState();
  const { project, videoShots } = projectState;
  const { audioClips } = useAudioStore.getState();
  const { comments, approvals } = useCommentStore.getState();
  if (!project) throw new Error('No project loaded');

  // Respect active scene filter — export only what the user is viewing
  const activeScenes = projectState.getActiveScenes();
  const activeShotIds = new Set(activeScenes.flatMap(s => s.shots.map(sh => sh.id)));

  // Build a lookup for video shots
  const videoMap = new Map(videoShots.map(v => [v.shotId, v]));

  // Movement arrows are composited at display time and are never baked into a render (movement is
  // absent from Unity's tile hash), so every surface that shows a shot has to draw them itself —
  // the exported player included. Geometry is resolved here so the 22-entry layout table stays in
  // one file; the player only positions what it is handed.
  const projectAr = parseAspectRatio(project.aspectRatio) || 16 / 9;
  const arrowSprites: string[] = [];
  const arrowSpriteIndex = new Map<string, number>();

  const scenes: ExportScene[] = activeScenes.map(scene => ({
    ...scene,
    shots: scene.shots.map(shot => {
      const thumbnail = shot.thumbnailDataUrl;

      const exportShot: ExportShot = {
        id: shot.id,
        sceneId: shot.sceneId,
        number: shot.number,
        name: shot.name,
        order: shot.order,
        duration: shot.duration,
        thumbnailDataUrl: thumbnail,
        notes: shot.notes,
        focalLength: shot.focalLength,
        fStop: shot.fStop,
        shotSize: shot.shotSize,
        shotAngle: shot.shotAngle,
        shotMovement: shot.shotMovement,
      };

      const arrows = computeMovementArrowLayout(
        shot.shotMovement ? shotMovementToNative(shot.shotMovement) : undefined,
        projectAr,
      );
      if (arrows.length > 0) {
        exportShot.movementArrows = arrows.map(a => {
          // Keyed by the data URI itself — ArrowLayout carries no type name, and one type maps to
          // exactly one sprite string, so the URI IS the type key here.
          let idx = arrowSpriteIndex.get(a.sprite);
          if (idx === undefined) {
            idx = arrowSprites.length;
            arrowSpriteIndex.set(a.sprite, idx);
            arrowSprites.push(a.sprite);
          }
          return {
            sprite: idx,
            leftPct: a.leftPct,
            topPct: a.topPct,
            widthPct: a.widthPct,
            heightPct: a.heightPct,
            transform: a.transform,
          };
        });
      }

      // Embed video as base64 data URL if opted in.
      //
      // `shot.videoObjectUrl` is the ONLY oracle for "this shot has video". The parallel
      // projectStore.videoShots array is derived exclusively from timeline_generation_history rows
      // with assetType 'video' (previsLoader.resolveHistoryToShots), so an ANIMATIC — which Unity
      // pushes as a bare file:// URL inside sceneReady, with no history row anywhere — never appears
      // in it. Gating on a videoShots row is what silently dropped every animatic from the export
      // while NLE export (which reads shotHasVideo → shot.videoObjectUrl) shipped them fine.
      // The row survives only as a MIME hint for the encode loop below.
      if (includeVideo && shot.videoObjectUrl) {
        const priv = exportShot as { _videoObjectUrl?: string; _videoMime?: string };
        priv._videoObjectUrl = shot.videoObjectUrl;
        priv._videoMime = videoMap.get(shot.id)?.mimeType;
      }

      return exportShot;
    }),
  }));

  // Thumbnails must leave here as data: URLs or not at all. In embedded mode the store holds
  // file:// URLs into Unity's cache (and requestShotFullImage answers with another file:// URL),
  // which are dead references once the HTML is saved, uploaded, or deployed. This one loop is why
  // Download HTML, ShareDialog (?session=) and Publish need no scheme handling of their own.
  //
  // The full-res pre-pass stays gated on compressImages: it exists so the compress-to-960px step
  // starts from Unity's original rather than its ~480p cache thumb. With compression OFF, pulling
  // full-res would inline uncompressed multi-megapixel stills — gigabytes for a large project.
  //
  // Sequential (not Promise.all) bounds the decode/IO spike on large projects.
  const shotById = new Map(activeScenes.flatMap(s => s.shots.map(sh => [sh.id, sh] as const)));
  for (const scene of scenes) {
    for (const shot of scene.shots) {
      if (!shot.thumbnailDataUrl) continue;
      let source = shot.thumbnailDataUrl;
      if (compressImages) {
        const storeShot = shotById.get(shot.id);
        if (storeShot) source = await resolveGenerationInputImage(storeShot, source);
      }
      const materialized = await materializeThumbnailDataUrl(source);
      if (!materialized) { shot.thumbnailDataUrl = undefined; continue; }
      shot.thumbnailDataUrl = compressImages
        ? await compressImageDataUrl(materialized)
        : materialized;
    }
  }

  // Resolve video blobs to base64 data URLs
  if (includeVideo) {
    for (const scene of scenes) {
      for (const shot of scene.shots) {
        const priv = shot as { _videoObjectUrl?: string; _videoMime?: string };
        const objUrl = priv._videoObjectUrl;
        if (objUrl) {
          try {
            const resp = await fetch(objUrl);
            const blob = await resp.blob();
            const arrayBuf = await blob.arrayBuffer();
            const bytes = new Uint8Array(arrayBuf);
            const b64 = uint8ToBase64(bytes);
            // blob.type is only trustworthy for a blob: URL — assetStorage.createAssetUrl stamps the
            // mime explicitly there. A file:// animatic goes through WKWebView's own type sniffing,
            // which may hand back an empty string; `data:;base64,…` yields a <video> that refuses to
            // play with no error. Hint first (the videoShots row, when there is one), then MP4 —
            // Unity's animatic renderer only ever writes MP4.
            shot.videoDataUrl = `data:${blob.type || priv._videoMime || 'video/mp4'};base64,${b64}`;
          } catch (e) {
            console.warn('Failed to encode video for shot', shot.id, e);
          }
        }
        delete priv._videoObjectUrl;
        delete priv._videoMime;
      }
    }
  }

  // Compute time ranges for active scenes so we can filter audio clips
  let sceneOffset = 0;
  const sceneTimeRanges: { start: number; end: number }[] = [];
  for (const scene of project.scenes) {
    if (scene.isOmitted) continue;
    const sceneDur = scene.shots.reduce((sum, s) => sum + s.duration, 0);
    if (activeScenes.some(as => as.id === scene.id)) {
      sceneTimeRanges.push({ start: sceneOffset, end: sceneOffset + sceneDur });
    }
    sceneOffset += sceneDur;
  }

  // Encode audio clips — only those overlapping active scenes.
  // In embedded mode the in-memory audioBlobStore is sparse (clips are
  // decoded lazily on play), so fall back to reading the full bytes from
  // SQLite. cdnUrl is also surfaced as mediaUrl so the player can stream
  // from the network if the embedded byte read fails for any reason.
  const overlappingClips = audioClips.filter(clip => {
    const absStart = getClipAbsoluteStart(clip, project);
    const clipEnd = absStart + clip.duration;
    return sceneTimeRanges.some(r => absStart < r.end && clipEnd > r.start);
  });

  // Ask Unity for anything the blob store is missing BEFORE deciding whether SQLite is needed.
  // A clip recorded by Unity's own mic never sends its bytes web-side (AudioWorkspace's embedded
  // branch stashes nothing), and the SQLite fallback below reads `_embeddedSqlitePath` — which on
  // the board route is board_slim.sqlite, a snapshot taken at project OPEN. Recording then exporting
  // without leaving the board therefore read a database that predates the recording and produced a
  // clip bar that draws its waveform and plays silence. The verb answers from the live open document,
  // and stashes, so every later consumer (publish, share, NLE) heals with it.
  //
  // Imported dynamically, like exportHtml's bridgeExportHtml below: a static edge from here would
  // drag the whole bridge graph (features → uiStore → embeddedBridge → every feature map) into the
  // exporter's module graph, which standalone builds never need and which every export suite would
  // then have to stand up.
  if (capabilities.bridge && overlappingClips.some(c => !getBlob(c.id))) {
    const { ensureAudioClipBytes } = await import('@/bridge/features/audio.ts');
    await ensureAudioClipBytes(overlappingClips.map(c => c.id));
  }

  let db: Awaited<ReturnType<typeof getEmbeddedDatabase>> | null = null;
  if (capabilities.bridge && overlappingClips.some(c => !getBlob(c.id))) {
    try {
      db = await getEmbeddedDatabase();
    } catch (err) {
      console.warn('[HTML Export] Failed to open SQLite for audio fallback:', err);
    }
  }

  const exportAudio: ExportAudioClip[] = overlappingClips.map(clip => {
    const absStart = getClipAbsoluteStart(clip, project);
    let raw = getBlob(clip.id);
    if (!raw && db) {
      raw = readAudioBytes(db, clip.id) ?? undefined;
    }
    const rawBase64 = raw
      ? `data:${clip.mimeType};base64,${uint8ToBase64(raw)}`
      : null;
    const mediaUrl = clip.cdnUrl ?? null;
    // Neither route produced audio. The player still draws this clip's bar and its peaks waveform
    // (renderAudioLane reads clip.peaks, not the bytes), so a silent export is indistinguishable
    // from a correct one — which is how this survived to a user. Name the clip loudly.
    if (rawBase64 === null && mediaUrl === null) {
      console.warn(`[HTML Export] audio clip ${clip.id} "${clip.label}" has no bytes and no CDN URL — it will export SILENT`);
    }
    return {
      id: clip.id,
      label: clip.label,
      startTime: absStart,
      duration: clip.duration,
      volume: clip.volume,
      mimeType: clip.mimeType,
      lane: clip.lane,
      isMuted: clip.isMuted,
      fadeInTime: clip.fadeInTime,
      fadeOutTime: clip.fadeOutTime,
      color: clip.color,
      // Seconds into the decoded buffer where this clip's content starts — non-zero for a
      // left-trimmed clip or the right half of a split. Without it the player reads from the buffer
      // head and plays the wrong region (and runs long); the live app has always applied it.
      bufferOffset: clip.bufferOffset ?? 0,
      rawBase64,
      mediaUrl,
      peaks: clip.peaks ? Array.from(clip.peaks) : null,
    };
  });

  if (db) db.close();

  // Compute duration and filter data to active scenes/shots only
  const exportDuration = scenes.reduce(
    (sum, s) => sum + s.shots.reduce((ss, sh) => ss + sh.duration, 0), 0
  );
  const exportTransitions = project.transitions.filter(
    t => activeShotIds.has(t.fromShotId) && activeShotIds.has(t.toShotId)
  );
  // Doodle strokes are opt-in (dialog checkbox, default off; publish passes nothing → off): comment
  // TEXT always exports, but with includeDoodles false the strokes must not ride along — the player
  // renders whatever annotationSvg it finds in __DATA__, so stripping here is the entire gate.
  const exportComments = comments
    .filter(c => c.shotId === null || activeShotIds.has(c.shotId))
    .map(c => (includeDoodles ? c : { ...c, annotationSvg: null }));
  const exportApprovals = approvals.filter(a => activeShotIds.has(a.shotId));

  return {
    title: project.title,
    aspectRatio: project.aspectRatio,
    sensorSize: project.sensorSize,
    hasScreenplay: project.hasScreenplay,
    scenes,
    totalDuration: exportDuration,
    transitions: exportTransitions,
    audioClips: exportAudio,
    comments: exportComments,
    approvals: exportApprovals,
    arrowSprites,
  };
}

// ── Estimate export size ─────────────────────────────────────────────────

export async function estimateExportSize(includeVideo: boolean, compressImages: boolean = true): Promise<{ thumbnailsMB: number; audioMB: number; videoMB: number; totalMB: number }> {
  const projectState = useProjectStore.getState();
  const { project } = projectState;
  const { audioClips } = useAudioStore.getState();
  if (!project) return { thumbnailsMB: 0, audioMB: 0, videoMB: 0, totalMB: 0 };

  // Respect active scene filter
  const activeScenes = projectState.getActiveScenes();

  // Thumbnails: data: URLs contribute their string length; file://+blob: references are measured
  // (see measureThumbnailBytes) — counting their string length is what reported 0.02 MB for a
  // 323-shot project.
  let thumbBytes = 0;
  for (const scene of activeScenes) {
    for (const shot of scene.shots) {
      if (shot.thumbnailDataUrl) thumbBytes += await measureThumbnailBytes(shot.thumbnailDataUrl);
    }
  }
  // Compression typically reduces JPEG thumbnails to ~25-35% of original
  if (compressImages) thumbBytes *= 0.3;

  // Compute time ranges for active scenes so we can filter audio clips
  const sceneTimeRanges: { start: number; end: number }[] = [];
  for (const scene of activeScenes) {
    // Calculate this scene's absolute offset by summing all prior scenes
    const priorDur = project.scenes
      .slice(0, project.scenes.indexOf(scene))
      .reduce((sum, s) => sum + s.shots.reduce((ss, sh) => ss + sh.duration, 0), 0);
    const sceneDur = scene.shots.reduce((sum, s) => sum + s.duration, 0);
    sceneTimeRanges.push({ start: priorDur, end: priorDur + sceneDur });
  }

  // Audio: estimate from blob store
  let audioBytes = 0;
  for (const clip of audioClips.filter(c => {
    const absStart = getClipAbsoluteStart(c, project);
    const clipEnd = absStart + c.duration;
    return sceneTimeRanges.some(r => absStart < r.end && clipEnd > r.start);
  })) {
    const raw = getBlob(clip.id);
    if (raw) {
      audioBytes += raw.byteLength * 1.37; // base64 overhead
    }
  }

  // Video: estimate from blob sizes (only for shots in active scenes).
  // Walks the SHOTS, not videoShots, for the same reason gatherExportData does — videoShots holds no
  // animatics, so an animatic-only project reported 0 MB here and ExportDialog then hid the Video row
  // outright (`sizes.videoMB > 0 && includeVideo`), under-reporting the very payload it is about to
  // inline. Same oracle as the gather step or the dialog lies about what the export will cost.
  let videoBytes = 0;
  if (includeVideo) {
    for (const scene of activeScenes) {
      for (const shot of scene.shots) {
        if (!shot.videoObjectUrl) continue;
        try {
          const resp = await fetch(shot.videoObjectUrl);
          const blob = await resp.blob();
          videoBytes += blob.size * 1.37; // base64 overhead
        } catch { /* skip */ }
      }
    }
  }

  const toMB = (b: number) => b / (1024 * 1024);
  const thumbnailsMB = toMB(thumbBytes);
  const audioMB = toMB(audioBytes);
  const videoMB = toMB(videoBytes);
  const totalMB = thumbnailsMB + audioMB + (includeVideo ? videoMB : 0) + 0.05; // ~50KB for HTML/CSS/JS

  return { thumbnailsMB, audioMB, videoMB, totalMB };
}

// ── Main export entry point ──────────────────────────────────────────────

export async function exportHtml(options: { includeVideo: boolean; compressImages: boolean; includeDoodles?: boolean }): Promise<void> {
  const data = await gatherExportData(options.includeVideo, options.compressImages, options.includeDoodles ?? false);
  const html = buildPlayerHtml(data);
  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const fileName = `${data.title}_${ts}.html`;

  if (capabilities.bridge) {
    const { bridgeExportHtml } = await import('@/bridge/embeddedBridge.ts');
    await bridgeExportHtml(html, fileName, data.title);
    return;
  }

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
